import { useEffect, useRef, useState } from "react";

/**
 * Live-caption capture using the browser's SpeechRecognition API.
 * Renders the rolling caption stream in a Teams-style overlay at the
 * bottom of the meeting view.
 *
 * Each finalised utterance is also handed back to the parent
 * (`onFinalized`) so the meeting can persist it as a transcript line
 * AND broadcast it to peers via the `meeting.caption` SignalR event.
 *
 * The Web Speech API only ships in Chromium-based browsers today
 * (Chrome / Edge / Brave / Opera). When unsupported, the component
 * renders a "Captions unavailable" hint instead of disappearing
 * silently — so the meeting host knows why the toggle did nothing.
 */
export function CaptionsOverlay({
    enabled,
    speakerName,
    incoming,
    onFinalized
}: {
    enabled: boolean;
    /** Local user's display name — labelled on locally-spoken lines. */
    speakerName: string;
    /** Live captions received from peers via SignalR. */
    incoming: ReadonlyArray<{ id: string; speaker: string; text: string }>;
    /** Fires for every finalised local utterance. */
    onFinalized: (text: string) => void;
}) {
    const recognitionRef = useRef<unknown>(null);
    const [supported, setSupported] = useState(true);
    const [interim, setInterim] = useState("");
    const [localLines, setLocalLines] = useState<Array<{ id: string; text: string }>>([]);

    useEffect(() => {
        if (!enabled) {
            setInterim("");
            return;
        }
        // SpeechRecognition / webkitSpeechRecognition are not in
        // lib.dom.d.ts; access them via the global window without
        // typings.
        const win = window as unknown as {
            SpeechRecognition?: new () => SpeechRecognitionLike;
            webkitSpeechRecognition?: new () => SpeechRecognitionLike;
        };
        const Ctor = win.SpeechRecognition ?? win.webkitSpeechRecognition;
        if (!Ctor) {
            setSupported(false);
            return;
        }
        const rec = new Ctor();
        rec.continuous = true;
        rec.interimResults = true;
        rec.lang = navigator.language || "en-US";

        rec.onresult = (event: SpeechRecognitionEventLike) => {
            let interimChunk = "";
            for (let i = event.resultIndex; i < event.results.length; i++) {
                const result = event.results[i]!;
                const transcript = result[0]?.transcript ?? "";
                if (result.isFinal) {
                    const trimmed = transcript.trim();
                    if (trimmed) {
                        setLocalLines((prev) => [
                            ...prev.slice(-4),
                            { id: `${Date.now()}-${i}`, text: trimmed }
                        ]);
                        onFinalized(trimmed);
                    }
                } else {
                    interimChunk += transcript;
                }
            }
            setInterim(interimChunk.trim());
        };
        rec.onerror = () => { /* drop silently — autoplay or NotAllowed */ };
        // Auto-restart on quiet pauses (Chrome stops after long silence).
        rec.onend = () => {
            if (recognitionRef.current === rec) {
                try { rec.start(); } catch { /* already started */ }
            }
        };

        recognitionRef.current = rec;
        try { rec.start(); } catch { /* already started */ }

        return () => {
            recognitionRef.current = null;
            try { rec.stop(); } catch { /* ignore */ }
        };
    }, [enabled, onFinalized]);

    if (!enabled) return null;

    const recentIncoming = incoming.slice(-4);

    return (
        <div className="pointer-events-none absolute inset-x-0 bottom-24 z-30 flex justify-center px-4">
            <div className="max-w-2xl rounded-md bg-black/70 px-4 py-2 text-sm text-white shadow-lg backdrop-blur-sm">
                {!supported ? (
                    <span className="text-xs text-white/70">
                        Captions are not supported in this browser. Try Chrome or Edge.
                    </span>
                ) : (
                    <>
                        {recentIncoming.map((line) => (
                            <div key={line.id} className="text-white/90">
                                <span className="font-semibold text-white">{line.speaker}: </span>
                                {line.text}
                            </div>
                        ))}
                        {localLines.map((line) => (
                            <div key={line.id} className="text-white/90">
                                <span className="font-semibold text-white">{speakerName}: </span>
                                {line.text}
                            </div>
                        ))}
                        {interim ? (
                            <div className="italic text-white/60">
                                <span className="font-semibold text-white/80">{speakerName}: </span>
                                {interim}…
                            </div>
                        ) : null}
                        {recentIncoming.length === 0 && localLines.length === 0 && !interim ? (
                            <span className="italic text-white/60">Listening for captions…</span>
                        ) : null}
                    </>
                )}
            </div>
        </div>
    );
}

// Minimal Web Speech API typings — keeping these local avoids polluting the
// project's lib.dom config with browser-prefixed entries.
interface SpeechRecognitionLike {
    continuous: boolean;
    interimResults: boolean;
    lang: string;
    onresult: ((event: SpeechRecognitionEventLike) => void) | null;
    onerror: ((event: unknown) => void) | null;
    onend: (() => void) | null;
    start(): void;
    stop(): void;
}

interface SpeechRecognitionEventLike {
    resultIndex: number;
    results: ArrayLike<{
        isFinal: boolean;
        0: { transcript: string };
        length: number;
    }>;
}
